import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";

import { services } from "../../../data/services";
import {
  BUDGET_OPTIONS,
  BUDGET_FACTORS,
  PAYMENT_OPTIONS,
} from "../../../data/constants";

const BudgetDiscussion = ({ projectType, formData, onUpdate, onNext }) => {
  const [selectedBudgetRange, setSelectedBudgetRange] = useState(
    formData.budgetRange || ""
  );
  const [budgetFactors, setBudgetFactors] = useState(
    formData.budgetFactors || []
  );
  const [paymentPreference, setPaymentPreference] = useState(
    formData.paymentPreference || ""
  );

  // ===== Find service by exact ID match =====
  const selectedService = services.find((s) => s.id === projectType);

  if (!selectedService) {
    console.error("Service not found for projectType:", projectType);
  }

  // ===== Use numeric price =====
  const serviceMinPrice = selectedService ? Number(selectedService.price) : 0;
 

  // ===== Filter budget options =====
  const filteredBudgetOptions = BUDGET_OPTIONS.filter(
    (option) =>
      option.id === "discuss" || Number(option.minValue) >= serviceMinPrice
  ).sort((a, b) => (a.minValue || 0) - (b.minValue || 0));

  

  // ===== Auto-select lowest valid tier =====
  useEffect(() => {
    if (!selectedBudgetRange && filteredBudgetOptions.length) {
      const lowest = filteredBudgetOptions[0];
      setSelectedBudgetRange(lowest.id);
      onUpdate({ budgetRange: lowest.id });
    }
  }, [projectType, filteredBudgetOptions]);

  const handleBudgetRangeSelect = (range) => {
    setSelectedBudgetRange(range.id);
    onUpdate({ budgetRange: range.id });
  };

  const handleBudgetFactorToggle = (factorId) => {
    const updated = budgetFactors.includes(factorId)
      ? budgetFactors.filter((id) => id !== factorId)
      : [...budgetFactors, factorId];

    setBudgetFactors(updated);
    onUpdate({ budgetFactors: updated });
  };

  const handlePaymentPreferenceSelect = (option) => {
    setPaymentPreference(option.id);
    onUpdate({ paymentPreference: option.id });
  };

  const handleContinue = () => {
    if (selectedBudgetRange) onNext();
  };

  if (!selectedService) return null; // don't render if service not found

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
          <Icon name="DollarSign" size={16} />
          <span>Investment Discussion</span>
        </div>

        <h2 className="text-3xl font-bold mb-4">Understanding Your Investment</h2>

        <p className="text-lg text-text-secondary max-w-2xl mx-auto">
          Transparent pricing aligned to real project scope.
        </p>
      </div>

      {/* Budget Selection */}
      <div className="mb-12">
        <h3 className="text-xl font-bold mb-6">Select Your Budget Range</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredBudgetOptions.map((range) => (
            <div
              key={range.id}
              onClick={() => handleBudgetRangeSelect(range)}
              className={`relative p-6 border-2 rounded-xl cursor-pointer transition ${
                selectedBudgetRange === range.id
                  ? "border-accent bg-accent/5"
                  : "border-border hover:border-accent/50"
              }`}
            >
              {range.popular && (
                <div className="absolute -top-3 left-6 bg-accent text-white px-3 py-1 rounded-full text-xs">
                  Most Popular
                </div>
              )}

              <div className="text-2xl font-bold text-accent mb-2">{range.range}</div>

              <h4 className="font-bold mb-2">{range.title}</h4>
              <p className="text-sm text-text-secondary mb-4">{range.description}</p>

              <ul className="space-y-1 mb-4">
                {range.includes.map((item, i) => (
                  <li key={i} className="flex items-center space-x-2 text-xs">
                    <Icon name="Check" size={12} className="text-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <p className="text-xs text-text-secondary">
                <strong>Best for:</strong> {range.bestFor}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Factors */}
      <div className="mb-12">
        <h3 className="text-xl font-bold mb-6">What Factors Influence Your Budget?</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {BUDGET_FACTORS.map((factor) => (
            <div
              key={factor.id}
              onClick={() => handleBudgetFactorToggle(factor.id)}
              className={`p-4 border rounded-lg cursor-pointer ${
                budgetFactors.includes(factor.id)
                  ? "border-accent bg-accent/5"
                  : "border-border"
              }`}
            >
              <h4 className="font-medium">{factor.label}</h4>
              <p className="text-sm text-text-secondary">{factor.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Payment */}
      <div className="mb-12">
        <h3 className="text-xl font-bold mb-6">Payment Preference</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PAYMENT_OPTIONS.map((option) => (
            <div
              key={option.id}
              onClick={() => handlePaymentPreferenceSelect(option)}
              className={`p-6 border-2 rounded-xl cursor-pointer ${
                paymentPreference === option.id
                  ? "border-accent bg-accent/5"
                  : "border-border"
              }`}
            >
              <h4 className="font-bold mb-2">{option.title}</h4>
              <p className="text-sm text-text-secondary mb-4">{option.description}</p>

              <div className="text-xs">
                <strong>Structure:</strong> {option.structure}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary */}
      {selectedBudgetRange && (
        <div className="bg-accent/5 border border-accent/20 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-bold mb-4 flex items-center space-x-2">
            <Icon name="Calculator" size={20} />
            <span>Investment Summary</span>
          </h3>

          <div className="text-lg font-bold text-accent">
            {filteredBudgetOptions.find((r) => r.id === selectedBudgetRange)?.range}
          </div>
        </div>
      )}

      {/* Continue */}
      <div className="flex justify-end">
        <button
          onClick={handleContinue}
          disabled={!selectedBudgetRange}
          className="px-8 py-4 rounded-lg font-semibold bg-accent text-white disabled:bg-gray-300"
        >
          Complete Booking
        </button>
      </div>
    </div>
  );
};

export default BudgetDiscussion;
